import { Component } from "react";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";

import { EditProject } from "./EditProject";

import { CountyProjectContext, ProjectOptions } from "@app/projects";
import { ResourceService, ResourceStatus, ResourceType } from "@app/resources";
import { RoleRestricted, Roles } from "@app/user";
import JobsPanelContent from "./JobsPanelContent";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { BreadcrumbTemplateEnum, breadcrumbTemplates } from "@app/shared";

interface ResourceControlsProps extends RouteComponentProps {
  defaultProjectOptionsOpen: boolean;
  isArchived: boolean;
  isProjectRunning: boolean;
  isReadOnly: boolean;
  isShared: boolean;
  resourceId: number;
  projectName: string;
}

interface ResourceControlState {
  projectOptionsDirty: boolean;
  sidePanelArchiveWarningVisible: boolean;
}

class ResourceControls extends Component<ResourceControlsProps, ResourceControlState> {
  private readonly resourceService = new ResourceService();

  constructor(props: ResourceControlsProps) {
    super(props);

    this.state = {
      projectOptionsDirty: false,
      sidePanelArchiveWarningVisible: false,
    };
  }

  public render() {
    const archiveIcon = this.props.isArchived ? "fa fa-sellsy" : "fa fa-archive";
    const archiveText = this.props.isArchived ? "Unarchive" : "Archive";

    return (
      <CountyProjectContext.Consumer>
        {({ project, runProject, forceUpdateProject, setProjectReadOnlyProperties }) => (
          <>
            <FlyoutLink
              content={{
                body: this.renderProjectOptions(project.projectId, runProject, setProjectReadOnlyProperties, forceUpdateProject),
                title: "Project Options",
              }}
              modalContent={{
                body: "You will lose unsaved changes. Continue with closing the sidebar?",
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                title: "Warning",
              }}
              isVisible={this.props.defaultProjectOptionsOpen}
              size="80"
              warnOnClose={this.state.projectOptionsDirty}
              testId="resource-controls__project-options-link"
              onClose={() => { this.setState({ projectOptionsDirty: false }) }}
            >
              <i className="fa fa-pencil-square-o" />{this.props.isShared ? " " : "Edit "}Project Options
            </FlyoutLink>
            {!this.props.isShared && (
              <>
                <span className="ml10">
                  <Link
                    to={{
                      pathname: `/resources/share`,
                      state: {
                        breadcrumbItems: [
                          ...breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment],
                          {
                            link: this.props.location.pathname,
                            text: this.props.projectName,
                          }
                        ],
                        isSingleResource: true,
                        resourceDescription: "Project",
                        resourceId: this.props.resourceId,
                        resourceTypes: [ResourceType.Project],
                      },
                    }}
                  >
                    <i className="fa fa-share" /> Share
                  </Link>

                </span>
                <ModalLink
                  classNames="ml10 side-panel-link"
                  disabled={this.props.isProjectRunning || (this.props.isReadOnly && !this.props.isArchived)}
                  content={{
                    body: `${archiveText} this project?`,
                    cancelButtonText: "Cancel",
                    confirmButtonAction: this.handleArchiveConfirm,
                    confirmButtonText: archiveText,
                    confirmButtonType: archiveText === "Archive" ? "primary-danger" : "primary",
                    title: archiveText,
                  }}
                  testId="resource-controls__archive-project-modal-link"
                >
                  <i className={`${archiveIcon}`} /> {archiveText}
                </ModalLink>
                <span className="ml10">
                  <Link
                    to={{
                      pathname: `/projects/${this.props.resourceId}/clone`,
                      state: {
                        linkToProject: true,
                      },
                    }}
                  >
                    <i className="fa fa-clone" /> Clone
                  </Link>
                </span>
                <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
                  <FlyoutLink
                    content={{
                      body: <JobsPanelContent projectId={project.projectId} projectName={project.resource.name} />,
                      title: `Jobs for Project ${project.projectId}: ${project.resource.name}`,
                    }}
                    testId="resource-controls__project-jobs-flyout-link"
                    classNames="ml10"
                    size="60"
                  >
                    <i className="fa fa-binoculars" /> Jobs
                  </FlyoutLink>
                </RoleRestricted>
              </>
            )}
          </>
        )}
      </CountyProjectContext.Consumer>
    );
  }

  private handleArchiveConfirm = async () => {
    if (!this.props.isProjectRunning) {
      if (this.props.isArchived) {
        await this.resourceService.updateResourceArchiveStatus(this.props.resourceId, ResourceStatus.Active);
      } else {
        await this.resourceService.updateResourceArchiveStatus(this.props.resourceId, ResourceStatus.Archived);
      }

      window.location.href = `${window.customUIBaseUrl}/`;
    }
  }

  private handleProjectOptionsChange = (projectOptionsDirty: boolean) => {
    if (this.state.projectOptionsDirty !== projectOptionsDirty) {
      this.setState({ projectOptionsDirty });
    }
  }

  private renderProjectOptions(projectId: number, runProject: () => Promise<void>, setProjectReadOnlyProperties: (isReadOnly: boolean, isEditRestricted: boolean, delay?: boolean) => ProjectOptions, forceUpdateProject?: () => Promise<void>) {
    return (
      <EditProject
        disabled={this.props.isShared}
        onChange={this.handleProjectOptionsChange}
        projectId={projectId}
        onRunProject={async () => {
          if (runProject) {
            await setProjectReadOnlyProperties(true, true);
            await runProject();
          }
        }}
        onSubmitSuccess={async () => {
          if (forceUpdateProject) {
            await forceUpdateProject();
          }
        }}
      />
    );
  }
}

export default withRouter(ResourceControls);
